import { getFragmentsFromSelection } from '@atlaskit/editor-common/selection';
import type { SelectionContext } from '@atlaskit/editor-common/types/selection';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import { type Selection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { isRemixRestrictedNodeType } from '@atlassian/malleable-ui-components/remix/remix-config';

import { normalizeRemixSelection } from '../../utils/normalize-remix-selection';

const sameFragment = (left: JSONNode[], right: JSONNode[]): boolean =>
	JSON.stringify(left) === JSON.stringify(right);

const hasSupportedText = (selection: TextSelection): boolean => {
	const { from, to } = selection;
	let found = false;
	selection.$from.doc.nodesBetween(from, to, (node, pos) => {
		if (found || isRemixRestrictedNodeType(node.type.name)) {
			return false;
		}
		if (node.isText) {
			const selectedText = (node.text ?? '').slice(Math.max(0, from - pos), to - pos);
			found = selectedText.replace(/\u200B/g, '').trim().length > 0;
		}
	});
	return found;
};

/**
 * Returns true only when the current selection is known to be unsupported. Stale cached context,
 * native embeds and classification failures leave the selection's eligibility unknown.
 */
export const isSelectionUnremixable = (
	selection: Selection | undefined,
	selectionContext: SelectionContext | null | undefined,
	isSelectionRemixable: (fragment: JSONNode[]) => boolean,
	normalizeSelection: boolean,
): boolean => {
	try {
		if (!(selection instanceof TextSelection) || selection.empty) {
			return false;
		}

		const normalizedSelection = normalizeSelection ? normalizeRemixSelection(selection) : selection;
		if (!(normalizedSelection instanceof TextSelection) || normalizedSelection.empty) {
			return false;
		}
		if (hasSupportedText(normalizedSelection)) {
			return false;
		}
		const currentFragment = getFragmentsFromSelection(normalizedSelection);
		if (currentFragment === null) {
			// The selected content could not be derived from the current editor state.
			return false;
		}

		if (currentFragment.some(containsNativePageEmbed)) {
			// Native embed content is not represented by this raw selected fragment.
			return false;
		}

		if (normalizedSelection === selection && selectionContext != null) {
			if (
				!Array.isArray(selectionContext.selectionFragment) ||
				!sameFragment(currentFragment, selectionContext.selectionFragment) ||
				selectionContext.startIndex !== normalizedSelection.$from.parentOffset ||
				selectionContext.endIndex !== normalizedSelection.$to.parentOffset
			) {
				// The request may use this cached context, so a mismatch cannot be classified safely.
				return false;
			}
		}

		return !isSelectionRemixable(currentFragment);
	} catch {
		// A failed selection check leaves eligibility unknown, so keep the entry point available.
		return false;
	}
};

const containsNativePageEmbed = (node: JSONNode): boolean => {
	const attrs = node.attrs as Record<string, unknown> | undefined;
	if (
		node.type === 'extension' &&
		typeof attrs?.extensionKey === 'string' &&
		attrs.extensionKey.startsWith('native-embed:')
	) {
		return true;
	}
	return (
		node.content?.some((child) => child !== undefined && containsNativePageEmbed(child)) ?? false
	);
};
